/**
 * Shorten urls.
 *
 * Returns a Promise that resolves with the short url as a string.
 *
 * Results are cached, and a same request done simultaneously will be
 * queued to return the same info as the already-running request.
 */

import type { ServiceDefinition } from '~publish/legacy/constants/services/types'
import callRpc from '~publish/legacy/utils/call-rpc'
import { logError } from '~publish/legacy/utils/logError'

// Map uses === semantics, so in order to compare by value, we're
// using a string hash rather than an object as the key. Domain is part of
// the key so a profile's shortening-domain change can never serve a link
// shortened under its old domain from cache.
function hashCacheKey(profileId: string, domain: string, url: string): string {
  return `${profileId}-${domain}-${url}`
}

const CHARS_LIMIT_SHORT_URLS = 22

const Shortener = {
  pendingRequests: new Map<string, Promise<string>>(),

  cache: new Map<string, string>(),

  /**
   * Shorten a url.
   *
   * Returns a Promise that resolves with the short url as a string.
   *
   * Results are cached, and a same request done simultaneously will be
   * queued to return the same info as the already-running request.
   *
   * ONLY call this method if you want to shorten links - do not call
   * in cases where you wish to rely on profile settings to determine shorting.
   *
   * `domain` is the profile's current shortening domain (e.g. 'bit.ly',
   * 'buff.ly'). It's cache-key-only — the backend picks the actual domain
   * from the profile's server-side settings — but it must be included so a
   * cached result from before the domain last changed never gets reused.
   */
  shorten(profileId: string, url: string, domain: string): Promise<string> {
    const params = {
      profile_id: profileId,
      url,
      // Force shortening - meaning even if
      // profile has shortening disabled, we'll use the default
      // link shortener to shorten.
      forceShortening: true,
    }

    const cacheKey = hashCacheKey(profileId, domain, url)

    // If the result is cached, return it right away
    const cachedShortLink = Shortener.cache.get(cacheKey)
    if (cachedShortLink !== undefined) {
      return Promise.resolve(cachedShortLink)
    }

    // If a request with the same params is already running, return a
    // Promise that'll resolve with that request's data
    const pendingRequest = Shortener.pendingRequests.get(cacheKey)
    if (pendingRequest !== undefined) {
      return pendingRequest
    }

    const request = callRpc('composerApiProxy', {
      url: '/1/links/shorten.json',
      args: params,
    }).then((result: { url: string }) => {
      const shortLink = result.url

      Shortener.cache.set(cacheKey, shortLink)
      if (shortLink !== url)
        Shortener.cache.set(
          hashCacheKey(profileId, domain, shortLink),
          shortLink,
        )

      Shortener.pendingRequests.delete(cacheKey)

      return shortLink
    })

    Shortener.pendingRequests.set(cacheKey, request)

    return request
  },

  warmCache(channelIds: string[], urls: string[], domain: string): void {
    const firstChannel = channelIds[0]
    for (const url of urls) {
      void Shortener.shorten(firstChannel, url, domain)
        .then((shortLink) => {
          for (const channelId of channelIds) {
            Shortener.cache.set(hashCacheKey(channelId, domain, url), shortLink)
            if (shortLink !== url) {
              Shortener.cache.set(
                hashCacheKey(channelId, domain, shortLink),
                shortLink,
              )
            }
          }
          return undefined
        })
        .catch(logError)
    }
  },

  clearCache(): void {
    Shortener.cache.clear()
  },

  isLinkTooShort(url: string): boolean {
    return url.length < CHARS_LIMIT_SHORT_URLS
  },

  shouldShorten(args: { service?: ServiceDefinition; url: string }): boolean {
    const { service, url } = args

    if (!service) {
      return true
    }

    if (!service.isShorteningLinksAvailable) {
      return false
    }

    if (Shortener.isLinkTooShort(url)) {
      return false
    }

    return true
  },
}

export default Shortener
